// frontend/src/pages/LinkedInFormatterPage.js
//
// /tools/linkedin-text-formatter
//
// MEASURED 2026-08-18. The highest commercial value in the feature queue:
// `linkedin text formatter` at CPC $59.05, KD 9, no AI Overview.
//
// FOUR KEYWORDS, ONE SERP. SERP-checked 2026-08-18 — `linkedin text formatter`,
// `linkedin formatter`, `linkedin post formatter` and `linkedin bold text` all
// return the SAME five URLs (typegrow, linkedin-makeover, authoredup, typefully,
// poper.ai) in slightly different orders. This is one page, not four.
//
// VOLUME CORRECTION, recorded 2026-08-18. feature-queue.yaml carries
// family_volume: 21590. That is inflated by Keyword Planner's close-variant
// grouping: `linkedin text formatter` and `linkedin formatter` are BOTH reported
// at exactly 5,400/mo at KD 9, and `linkedin post formatter` and `linkedin post
// formatting` BOTH at exactly 1,900 with the same $23.68 CPC. Those are the same
// buckets counted twice. Realistic distinct volume is ~9,000/mo. Still the best
// commercial term we have measured; just not 21,590.
//
// WALL = 14, re-measured 2026-08-18: typegrow 21, linkedin-makeover 35,
// authoredup 7, typefully 5. The last two are publishing tools that sell
// scheduling — our own category — holding page one on 5 and 7 referring domains.
//
// THE DIFFERENTIATOR IS HONESTY, NOT FEATURES. Every competitor here sells the
// formatting without mentioning that Unicode look-alikes are unreadable to
// screen readers and invisible to LinkedIn search. Verified 2026-08-18 against
// Scope for Business and RNIB, both of which advise against Unicode text on
// social media. We ship the same formatting AND measure the damage.
import '../custom.css'

import React from 'react'
import { Container, Row, Col, Button, Accordion, Image } from 'react-bootstrap'
import { CheckCircleFill } from 'react-bootstrap-icons'

import Seo from '../components/Seo'
import { getSeoMeta } from '../data/seoMeta'
import LinkedInFormatter from '../components/LinkedInFormatter'

const URL = '/tools/linkedin-text-formatter'
const HERO = '/assets/images/articles/linkedin-formatter.webp'

// This array drives the visible accordion. The FAQPage schema in seo-meta.json
// was GENERATED from it, so the two match today — but nothing enforces that.
// After editing this array, regenerate the schema rather than hand-editing it:
//
//   node -e "const fs=require('fs');const s=fs.readFileSync('frontend/src/pages/LinkedInFormatterPage.js','utf8');
//   const o=s.indexOf('[',s.indexOf('const FAQS'));let d=0,e=-1;
//   for(let i=o;i<s.length;i++){if(s[i]==='[')d++;else if(s[i]===']'&&--d===0){e=i;break}}
//   const F=eval(s.slice(o,e+1));const m=JSON.parse(fs.readFileSync('frontend/src/data/seo-meta.json','utf8'));
//   m.routes['/tools/linkedin-text-formatter'].jsonLd[0].mainEntity=F.map(([name,text])=>
//     ({'@type':'Question',name,acceptedAnswer:{'@type':'Answer',text}}));
//   fs.writeFileSync('frontend/src/data/seo-meta.json',JSON.stringify(m,null,2)+'\n')"
export const FAQS = [
  ['Does LinkedIn support bold text?',
   'Not natively. LinkedIn posts have no rich-text formatting, so there is no bold button. What everyone uses instead is a set of Unicode characters that look like bold letters — the letter A is swapped for a different character that happens to be drawn in bold. Nothing is actually styled; the alphabet is replaced.'],
  ['How do I bold text in a LinkedIn post?',
   'Paste or type your post into the formatter above, select the words you want to emphasise, and click Bold. Then copy the result and paste it into LinkedIn. The characters carry their appearance with them, so no formatting is lost in the paste.'],
  ['Is bold text on LinkedIn bad for accessibility?',
   'Yes, and this is the part most formatters do not tell you. Screen readers announce these characters by their Unicode names — "mathematical bold capital A" — or skip them altogether. Scope and RNIB both advise against Unicode styling on social media. A short bolded phrase is a reasonable trade; a whole post written in look-alikes is genuinely unreadable to some of your audience. The tool above measures what percentage of your post is affected.'],
  ['Does formatted text hurt my reach on LinkedIn?',
   'It can. LinkedIn search matches real characters, so any word written in pseudo-bold is invisible to someone searching that term. If your headline contains the keywords you want to be found for, formatting them is the one place it actively costs you.'],
  ['How do I add bullet points to a LinkedIn post?',
   'Type the bullet character at the start of each line — the formatter preserves any character you paste in. Bullets are ordinary Unicode symbols rather than look-alike letters, so unlike pseudo-bold they are read correctly by screen readers and cost you nothing.'],
  ['Can I use italic and underline as well?',
   'Yes. Italic works the same way as bold, with its own character block. Underline and strikethrough are different — they are combining marks that attach to each letter rather than replacing it, which is why they sometimes render inconsistently across apps and devices.'],
  ['Where does LinkedIn cut a post off with "see more"?',
   'At roughly 210 characters on desktop, and sooner on mobile. LinkedIn does not publish the exact number and it has changed before. The preview above shows the split so you can see which sentence is doing the work of earning the click.'],
  ['Will the formatting survive being pasted into LinkedIn?',
   'Yes. Because the styling is the characters themselves rather than markup, it survives copy and paste everywhere — LinkedIn, the mobile app, comments, even a different site entirely. That is also precisely why it cannot be undone by LinkedIn, and why screen readers see it.'],
]

export default function LinkedInFormatterPage () {
  return (
    <>
      {/* Title, description and the FAQ/WebApplication schema all come from
          seo-meta.json — the same table the prerenderer bakes into the static
          head — so the runtime and prerendered heads cannot drift. Passing only
          `path` here left the title undefined, which react-helmet resolved to
          the bare brand name and the build verifier caught. */}
      <Seo {...getSeoMeta(URL)} image={HERO} />

      {/* ───────── HERO ───────── */}
      <section className='full-bleed py-5 bg-primary-soft'>
        <Container fluid='lg'>
          <Row className='align-items-center g-4'>
            <Col lg={7}>
              <h1 className='display-5 fw-bold mb-3'>LinkedIn text formatter</h1>
              <p className='lead mb-0'>
                Bold, italic and underline for LinkedIn posts — with a preview of where the
                “…see more” fold lands, and an honest measure of what the formatting costs you.
              </p>
            </Col>
            <Col lg={5}>
              <Image src={HERO} alt='Someone drafting a post at a laptop' fluid rounded
                     loading='eager' width={1600} height={1066} className='shadow-sm' />
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── THE ANSWER FIRST ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          <p className='fs-5'>
            <strong>LinkedIn has no bold button.</strong> Posts carry no rich-text formatting at
            all, so every bold LinkedIn post you have seen is a trick: the letters have been
            swapped for different Unicode characters that happen to be drawn in bold. Nothing is
            styled — the alphabet is replaced.
          </p>
          <p className='text-muted fs-5 mb-0'>
            The formatter below does that swap. It also does two things the other tools do not:
            it shows you where LinkedIn folds your post behind “…see more”, and it measures how
            much of your text has become unreadable to screen readers and invisible to LinkedIn
            search. Both matter more than the bold does.
          </p>
        </Container>
      </section>

      {/* ───────── THE TOOL ───────── */}
      <section className='pb-5'>
        <Container fluid='lg' style={{ maxWidth: 1000 }}>
          <LinkedInFormatter />
        </Container>
      </section>

      {/* ───────── WHAT LINKEDIN ACTUALLY SUPPORTS ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg' style={{ maxWidth: 900 }}>
          <h2 className='fw-bold mb-4'>What LinkedIn actually supports</h2>
          <div className='table-responsive'>
            <table className='table table-bordered align-middle bg-white mb-0'>
              <tbody>
                {[
                  ['Native bold or italic', 'None', 'No formatting toolbar exists in the post composer.'],
                  ['What the tools do', 'Swap the characters', 'A is replaced by a different codepoint that looks bold.'],
                  ['Post length', '3,000 characters', 'Counted in characters, not words.'],
                  ['The “see more” fold', '~210 characters', 'Not published by LinkedIn, and shorter on mobile.'],
                  ['Screen readers', 'Cannot read it', 'Announced by Unicode name, or skipped entirely.'],
                  ['LinkedIn search', 'Cannot match it', 'Styled words are invisible to anyone searching them.'],
                  ['Bullet points', 'Work normally', 'Ordinary symbols — no accessibility cost.'],
                  ['Surviving a paste', 'Always', 'The style is the character, so it travels everywhere.'],
                ].map(([label, value, note]) => (
                  <tr key={label}>
                    <td className='fw-semibold' style={{ width: '26%' }}>{label}</td>
                    <td className='text-primary fw-semibold' style={{ width: '24%' }}>{value}</td>
                    <td className='text-muted'>{note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Container>
      </section>

      {/* ───────── DEPTH ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          {[
            ['The cost nobody selling these tools mentions',
             'Screen readers do not see bold text — they see a string of characters named "mathematical sans-serif bold capital A" and either read that aloud or skip the word. Scope, the disability equality charity, and RNIB both advise against Unicode styling on social media for exactly this reason. On LinkedIn specifically, where a meaningful share of your audience may be job seekers using assistive technology, a post written entirely in pseudo-bold is not a stylistic choice — it is a post some people cannot read. Short emphasis is a fair trade. A whole post is not, and the meter above will tell you which one you have written.'],
            ['Formatted words are invisible to LinkedIn search',
             'LinkedIn indexes the actual characters. A headline that says you are a "𝗳𝗿𝗮𝗰𝘁𝗶𝗼𝗻𝗮𝗹 𝗖𝗧𝗢" will not be returned to anyone searching for a fractional CTO, because those are not the letters they typed. This is the one place formatting reliably costs you something measurable, and it is why the profile headline is the worst place on LinkedIn to use it.'],
            ['The fold is where the post is won',
             'LinkedIn shows roughly 210 characters before "…see more", and almost nobody expands a post they are not already interested in. That makes the first two lines the entire job — not the formatting further down. If you only change one thing about how you write on LinkedIn, make it the opening line rather than the styling, and use the preview above to see exactly where the cut falls.'],
            ['Where formatting genuinely helps',
             'Short labels in a list, a single emphasised phrase in a long post, and section headers in a genuinely long piece — these are cheap and they aid scanning. What does not work is bolding whole paragraphs, which reads as shouting, defeats the purpose of emphasis, and takes the accessibility cost to its maximum for no gain.'],
          ].map(([heading, body]) => (
            <div className='mb-4' key={heading}>
              <h2 className='fw-bold h4 mb-2'>{heading}</h2>
              <p className='text-muted mb-0'>{body}</p>
            </div>
          ))}
        </Container>
      </section>

      {/* ───────── WHAT IS GREATLY SOCIAL (standing rule 4) ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg' style={{ maxWidth: 940 }}>
          <Row className='align-items-center g-4'>
            <Col md={6}>
              <h2 className='fw-bold mb-3'>What is Greatly Social?</h2>
              <p className='text-muted'>
                Greatly Social is a social media scheduler and content calendar. At Greatly
                Social you can write a LinkedIn post, have AI draft the version for every other
                network, and schedule the lot from one calendar instead of posting by hand.
              </p>
              <p className='text-muted'>
                Formatting a post takes a minute. Posting consistently for a year is the part
                that actually builds an audience. Plans start at $9/month and the trial needs
                no card.
              </p>
              <Button variant='primary' href='/register' className='me-2'>
                Start using Greatly Social free
              </Button>
              <Button variant='outline-primary' href='/plans'>See plans</Button>
            </Col>
            <Col md={6}>
              {/* A real capture of the product, not stock — standing rule 2. */}
              <Image src='/assets/logo/aipost.webp' alt='Drafting a post in Greatly Social'
                     fluid rounded className='shadow-sm' loading='lazy' />
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── CROSS-LINKS (standing rule 8) ───────── */}
      <section className='py-5'>
        <Container fluid='lg' style={{ maxWidth: 880 }}>
          <h2 className='fw-bold h4 mb-3'>More free tools</h2>
          <Row className='g-3'>
            {[['/linkedin-scheduler', 'LinkedIn post scheduler', 'Schedule posts in advance'],
              ['/tools/linkedin-bio-generator', 'LinkedIn bio generator', 'Written to the character limit'],
              ['/tools/linkedin-banner-size', 'LinkedIn banner size', '1584×396, plus a resizer'],
              ['/tools/instagram-name-generator', 'Name generators', 'Handles for every network'],
              ['/tools/instagram-caption-generator', 'Caption generator', 'Every network at once'],
              ['/tools', 'All free tools', 'The full set']].map(([href, title, note]) => (
              <Col md={4} key={href}>
                <a href={href} className='d-block p-3 rounded-3 text-decoration-none h-100'
                   style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
                  <div className='fw-semibold'>{title}</div>
                  <div className='text-muted small'>{note}</div>
                </a>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ───────── PRODUCT ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg' style={{ maxWidth: 880 }}>
          <h2 className='fw-bold mb-3'>The formatting is the easy part</h2>
          <p className='text-muted fs-5'>
            Posting on LinkedIn twice a week for a year is what moves anything. That is a
            scheduling problem, not a styling one.
          </p>
          <Row className='g-3 mb-4'>
            {[['Write once, post everywhere', 'A separate version per network from one draft.'],
              ['Schedule weeks ahead', 'Set your slots and let them run.'],
              ['A real content calendar', 'Drag to reschedule. Empty days are obvious.'],
              ['AI drafts, you edit', 'Describe the business once and get the month.']].map(([t, b]) => (
              <Col md={6} key={t}>
                <div className='d-flex align-items-start gap-2'>
                  <CheckCircleFill className='text-success mt-1 flex-shrink-0' />
                  <div><div className='fw-semibold'>{t}</div><div className='text-muted small'>{b}</div></div>
                </div>
              </Col>
            ))}
          </Row>
          <Button variant='primary' size='lg' href='/register' className='me-2'>
            Start using Greatly Social free
          </Button>
          <Button variant='outline-primary' size='lg' href='/linkedin-scheduler'>
            See the LinkedIn scheduler
          </Button>
        </Container>
      </section>

      {/* ───────── FAQ (standing rule 5) ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          <h2 className='fw-bold mb-4 text-center'>Common questions</h2>
          <Accordion flush defaultActiveKey='0'>
            {FAQS.map(([q, a], i) => (
              <Accordion.Item eventKey={String(i)} key={i}>
                <Accordion.Header>{q}</Accordion.Header>
                <Accordion.Body className='text-muted'>{a}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Container>
      </section>

      {/* ───────── CTA (standing rule 3) ───────── */}
      <section className='full-bleed py-5 text-center bg-primary text-white'>
        <div className='mx-auto px-4' style={{ maxWidth: 720 }}>
          <h2 className='fw-bold mb-3'>At Greatly Social you can schedule the post, not just style it</h2>
          <p className='lead mb-4'>
            Draft it once, let AI adapt it for every other network, and schedule the month from
            one calendar. Free for 14 days, no card, plans from $9/month.
          </p>
          <Button variant='light' size='lg' href='/register' className='me-2 mb-2'>
            Start using Greatly Social free
          </Button>
          <Button variant='outline-light' size='lg' href='/calendar-view' className='mb-2'>
            See the calendar
          </Button>
        </div>
      </section>
    </>
  )
}
